import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI, compatAPI } from '../../utils/api';
import StatCard from '../../components/StatCard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import useReleaseFilter from '../../hooks/useReleaseFilter';
import ReleaseFilterBar from '../../components/ReleaseFilterBar';

export default function CastingAnalyticsPage() {
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [timeSeries, setTimeSeries] = useState([]);
    const [timeframe, setTimeframe] = useState('7d');
    const [storageErrors, setStorageErrors] = useState(null);
    const [discoveryCensus, setDiscoveryCensus] = useState(null);
    const [transcodeEvents, setTranscodeEvents] = useState(null);
    const [remoteSourceEvents, setRemoteSourceEvents] = useState(null);
    const [ccDisconnectReasons, setCcDisconnectReasons] = useState(null);
    const [loading, setLoading] = useState(true);
    const [appVersions, setAppVersions] = useState([]);
    const [currentVersion, setCurrentVersion] = useState(null);
    const filter = useReleaseFilter();

    // Builds-seen KPI strip is fetched separately so the chip row is
    // available even before the first page payload returns.
    useEffect(() => {
        compatAPI.getBuildsSeen(filter.days).then(r => {
            setAppVersions(r.data?.app_versions || []);
            setCurrentVersion(r.data?.current_app_version_name || null);
        }).catch(() => {});
    }, [filter.days]);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeframe, filter.queryParams.release_op, filter.queryParams.release_value, filter.queryParams.days, filter.queryParams.layer]);

    const loadData = async () => {
        setLoading(true);
        try {
            // The page has TWO time controls now — its own legacy
            // timeframe enum (24h/7d/30d/all), and the ReleaseFilterBar
            // window. The bar's `days` overrides the legacy timeframe by
            // being passed as `extra` to the API helpers. The legacy enum
            // still drives the timeframe label in the summary header.
            const params = filter.queryParams;
            const [summaryRes, sessionsRes, timeSeriesRes, storageRes, censusRes, transcodeRes, remoteRes, ccDisconnectRes] = await Promise.all([
                analyticsAPI.getSummary(timeframe, params),
                analyticsAPI.getSessions(20, 0),
                analyticsAPI.getTimeSeries(filter.days || 7, params),
                analyticsAPI.getStorageErrors(filter.days || 30, params).catch(() => ({ data: null })),
                analyticsAPI.getDiscoveryCensus(filter.days || 7, params).catch(() => ({ data: null })),
                analyticsAPI.getTranscodeEvents(filter.days || 30, params).catch(() => ({ data: null })),
                analyticsAPI.getRemoteSourceEvents(filter.days || 30, params).catch(() => ({ data: null })),
                analyticsAPI.getCcDisconnectReasons(filter.days || 30, params).catch(() => ({ data: null })),
            ]);
            setSummary(summaryRes.data);
            setSessions(sessionsRes.data);
            setTimeSeries(timeSeriesRes.data);
            setStorageErrors(storageRes.data);
            setDiscoveryCensus(censusRes.data);
            setTranscodeEvents(transcodeRes.data);
            setRemoteSourceEvents(remoteRes.data);
            setCcDisconnectReasons(ccDisconnectRes.data);
        } catch (error) {
            console.error('Error loading casting data:', error);
        } finally {
            setLoading(false);
        }
    };

    const exportCSV = async () => {
        try {
            const response = await analyticsAPI.exportCSV();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `zebcast-analytics-${Date.now()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error exporting CSV:', error);
        }
    };

    const formatDuration = (seconds) => {
        if (!seconds) return '0s';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hours > 0) return `${hours}h ${minutes}m`;
        if (minutes > 0) return `${minutes}m ${secs}s`;
        return `${secs}s`;
    };

    const getProtocolBadgeColor = (protocol) => {
        switch (protocol?.toLowerCase()) {
            case 'chromecast': return 'bg-blue-100 text-blue-800';
            case 'dlna': return 'bg-green-100 text-green-800';
            case 'miracast': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading && !summary) return <div className="text-gray-400">Loading...</div>;

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Casting Analytics</h2>
                <div className="flex gap-4">
                    <select
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-zebcast-purple"
                    >
                        <option value="24h">Last 24 Hours</option>
                        <option value="7d">Last 7 Days</option>
                        <option value="30d">Last 30 Days</option>
                        <option value="all">All Time</option>
                    </select>
                    <button
                        onClick={exportCSV}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                        Export CSV
                    </button>
                </div>
            </div>

            <ReleaseFilterBar
                filter={filter}
                appVersions={appVersions}
                currentVersion={currentVersion} />

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard title="Total Sessions" value={summary?.totalSessions || 0} subtitle="Cast sessions" icon="TV" />
                <StatCard title="Videos Played" value={summary?.totalVideos || 0} subtitle="Media items" icon="Film" />
                <StatCard title="Total Playtime" value={formatDuration(summary?.totalPlaytime || 0)} subtitle="Watching time" icon="Clock" />
                <StatCard title="Errors" value={summary?.totalErrors || 0} subtitle="Total errors" icon="Alert" />
            </div>

            {/* Protocol Breakdown */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Protocol Usage</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(summary?.protocolBreakdown || {}).map(([protocol, count]) => (
                        <div key={protocol} className="text-center p-4 bg-gray-50 rounded-lg">
                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getProtocolBadgeColor(protocol)}`}>
                                {protocol.toUpperCase()}
                            </span>
                            <p className="text-2xl font-bold text-gray-900 mt-2">{count}</p>
                            <p className="text-sm text-gray-600">sessions</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Time Series Chart */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Sessions Over Time</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={timeSeries}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="count" stroke="#667eea" strokeWidth={2} />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Transcode & Format Compatibility */}
            {transcodeEvents && (transcodeEvents.aggregates?.length > 0 || transcodeEvents.format_by_device?.length > 0) && (
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Transcode & Format Compatibility
                        <span className="ml-2 text-sm font-normal text-gray-500">(last 30 days)</span>
                    </h3>

                    {/* Summary by event type and result */}
                    {transcodeEvents.aggregates?.length > 0 && (
                        <div className="overflow-x-auto mb-4">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Devices</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Avg Duration</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {transcodeEvents.aggregates.map((row, i) => (
                                        <tr key={i} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 text-sm font-mono">
                                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                                    row.event_type === 'format_incompatibility' ? 'bg-red-100 text-red-800' :
                                                    row.event_type === 'live_transcode' ? 'bg-blue-100 text-blue-800' :
                                                    row.event_type === 'offline_transcode' ? 'bg-green-100 text-green-800' :
                                                    'bg-yellow-100 text-yellow-800'
                                                }`}>{row.event_type}</span>
                                            </td>
                                            <td className="px-4 py-2 text-sm">
                                                <span className={`font-semibold ${
                                                    row.result === 'success' || row.result === 'started' ? 'text-green-600' :
                                                    row.result === 'error' || row.result === 'too_slow' ? 'text-red-600' :
                                                    'text-gray-600'
                                                }`}>{row.result}</span>
                                            </td>
                                            <td className="px-4 py-2 text-sm font-semibold">{row.count}</td>
                                            <td className="px-4 py-2 text-sm">{row.unique_devices}</td>
                                            <td className="px-4 py-2 text-sm">{row.avg_duration_ms ? Math.round(row.avg_duration_ms / 1000) + 's' : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Format incompatibility by device */}
                    {transcodeEvents.format_by_device?.length > 0 && (
                        <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-2">Format Rejections by Device</h4>
                            <div className="flex flex-wrap gap-2">
                                {transcodeEvents.format_by_device.map((row, i) => (
                                    <span key={i} className="inline-block px-2 py-1 rounded bg-red-50 text-red-800 text-xs">
                                        <strong>{row.device_name}</strong>: {row.mime_type} ({row.count}x)
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Recent events */}
                    {transcodeEvents.events?.length > 0 && (
                        <details className="mt-4">
                            <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                                Recent events ({transcodeEvents.events.length})
                            </summary>
                            <div className="mt-2 max-h-64 overflow-y-auto">
                                {transcodeEvents.events.slice(0, 30).map((evt, i) => (
                                    <div key={i} className="py-1 border-b border-gray-100 text-xs">
                                        <span className="text-gray-400">{new Date(evt.timestamp).toLocaleString()}</span>
                                        <span className={`ml-2 font-mono px-1 rounded ${
                                            evt.event_type === 'format_incompatibility' ? 'bg-red-50 text-red-700' :
                                            evt.event_type === 'live_transcode' ? 'bg-blue-50 text-blue-700' :
                                            evt.event_type === 'offline_transcode' ? 'bg-green-50 text-green-700' :
                                            'bg-yellow-50 text-yellow-700'
                                        }`}>{evt.event_type}</span>
                                        {evt.properties?.result && <span className="ml-1 font-semibold">{evt.properties.result}</span>}
                                        {evt.properties?.device_name && <span className="ml-1 text-gray-500">{evt.properties.device_name}</span>}
                                        {evt.properties?.file_name && <span className="ml-1 text-gray-400">{evt.properties.file_name}</span>}
                                        {evt.properties?.duration_ms > 0 && <span className="ml-1 text-gray-500">({Math.round(evt.properties.duration_ms / 1000)}s)</span>}
                                        {evt.phone_model && <span className="ml-1 text-gray-400">[{evt.phone_manufacturer} {evt.phone_model}]</span>}
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                </div>
            )}

            {/* Discovery Census */}
            {discoveryCensus && discoveryCensus.latest_per_device?.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Discovered Devices
                        <span className="ml-2 text-sm font-normal text-gray-500">(last 7 days, per user)</span>
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Device (Phone)</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">DLNA</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Miracast</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Chromecast</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ZCD</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Discovered Sinks</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Seen</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {discoveryCensus.latest_per_device.map((row, i) => {
                                    const p = row.properties || {};
                                    let sinks = [];
                                    try { sinks = JSON.parse(p.devices || '[]'); } catch(e) {}
                                    return (
                                        <tr key={i} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 text-sm text-gray-900">
                                                {row.phone_manufacturer} {row.phone_model}
                                                <span className="block text-xs text-gray-400 font-mono">{row.device_id?.substring(0, 8)}</span>
                                            </td>
                                            <td className="px-4 py-2 text-sm font-semibold text-gray-900">{p.total_devices || 0}</td>
                                            <td className="px-4 py-2 text-sm text-green-700">{p.dlna_count || 0}</td>
                                            <td className="px-4 py-2 text-sm text-yellow-700">{p.miracast_count || 0}</td>
                                            <td className="px-4 py-2 text-sm text-blue-700">{p.chromecast_count || 0}</td>
                                            <td className="px-4 py-2 text-sm text-purple-700">{p.zcd_count || 0}</td>
                                            <td className="px-4 py-2 text-xs text-gray-600">
                                                {sinks.map((s, j) => (
                                                    <span key={j} className={`inline-block mr-1 mb-1 px-1.5 py-0.5 rounded text-xs ${
                                                        s.type === 'dlna' ? 'bg-green-100 text-green-800' :
                                                        s.type === 'miracast' || s.type === 'miracast_system' ? 'bg-yellow-100 text-yellow-800' :
                                                        s.type === 'chromecast' ? 'bg-blue-100 text-blue-800' :
                                                        s.type === 'zcd' ? 'bg-purple-100 text-purple-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>{s.name}</span>
                                                ))}
                                            </td>
                                            <td className="px-4 py-2 text-xs text-gray-500">{row.timestamp ? new Date(row.timestamp).toLocaleString() : '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Storage Access Issues */}
            {storageErrors && (storageErrors.aggregates?.length > 0 || parseInt(storageErrors.sessionErrors?.count) > 0) && (
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Storage Access Issues
                        <span className="ml-2 text-sm font-normal text-gray-500">(last 30 days)</span>
                    </h3>
                    {parseInt(storageErrors.sessionErrors?.count) > 0 && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-r-lg mb-4">
                            <p className="text-red-800 text-sm font-semibold">
                                {storageErrors.sessionErrors.count} session failures from STORAGE_ACCESS_DENIED
                                <span className="font-normal text-red-600 ml-1">
                                    ({storageErrors.sessionErrors.unique_devices} unique devices)
                                </span>
                            </p>
                        </div>
                    )}
                    {storageErrors.aggregates?.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">API Level</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Removable Storage</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Devices</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {storageErrors.aggregates.map((row, i) => (
                                        <tr key={i} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 text-sm font-mono text-gray-900">{row.reason}</td>
                                            <td className="px-4 py-2 text-sm text-gray-900">{row.api_level}</td>
                                            <td className="px-4 py-2 text-sm text-gray-900">{row.is_removable === 'true' ? 'Yes (SD card)' : 'No'}</td>
                                            <td className="px-4 py-2 text-sm font-semibold text-gray-900">{row.count}</td>
                                            <td className="px-4 py-2 text-sm text-gray-900">{row.unique_devices}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {storageErrors.events?.length > 0 && (
                        <details className="mt-4">
                            <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                                Recent events ({storageErrors.events.length})
                            </summary>
                            <div className="mt-2 max-h-64 overflow-y-auto">
                                {storageErrors.events.slice(0, 20).map((evt, i) => (
                                    <div key={i} className="py-1 border-b border-gray-100 text-xs">
                                        <span className="text-gray-400">{new Date(evt.timestamp).toLocaleString()}</span>
                                        <span className="ml-2 font-mono text-gray-700">{evt.event_type}</span>
                                        {evt.phone_model && <span className="ml-2 text-gray-500">{evt.phone_manufacturer} {evt.phone_model}</span>}
                                        {evt.properties?.reason && <span className="ml-2 text-red-600">{evt.properties.reason}</span>}
                                        {evt.properties?.file_ext && <span className="ml-1 text-gray-400">({evt.properties.file_ext})</span>}
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                </div>
            )}

            {/* Recent Sessions Table */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Sessions</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Protocol</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sink Device</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Result</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Connect</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Play</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {sessions.map((session) => (
                                <tr key={session.session_id} className="hover:bg-blue-50 cursor-pointer" onClick={() => navigate(`/admin/session/${session.session_id}`)}>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                        {session.timestamp_start ? new Date(session.timestamp_start).toLocaleString() : 'N/A'}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getProtocolBadgeColor(session.protocol)}`}>
                                            {(session.protocol || 'local').toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{session.sink_name || 'Local'}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${session.result === 'success' ? 'bg-green-100 text-green-800' : session.result === 'error' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                            {session.result}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-red-600">{session.error_code ? `${session.error_code} @ ${session.error_stage || '?'}` : '—'}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{session.connect_time_ms ? `${session.connect_time_ms}ms` : '—'}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{session.total_play_time_s ? `${session.total_play_time_s}s` : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Chromecast Disconnect Reasons */}
            {ccDisconnectReasons && ccDisconnectReasons.total > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                        Chromecast Disconnect Reasons
                        <span className="ml-2 text-sm font-normal text-gray-500">(last 30 days)</span>
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                        {ccDisconnectReasons.total} disconnects across {ccDisconnectReasons.unique_senders} sender{ccDisconnectReasons.unique_senders !== 1 ? 's' : ''}
                    </p>

                    {/* Reason breakdown — colour-coded pills */}
                    <div className="flex flex-wrap gap-2 mb-5">
                        {ccDisconnectReasons.by_reason.map((row, i) => {
                            const pct = ccDisconnectReasons.total > 0
                                ? Math.round((row.count / ccDisconnectReasons.total) * 100) : 0;
                            const color =
                                row.reason === 'session_suspended'   ? 'bg-orange-100 text-orange-800 border-orange-300' :
                                row.reason === 'receiver_abort'      ? 'bg-red-100 text-red-800 border-red-300' :
                                row.reason === 'session_ended'       ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                row.reason === 'receiver_canceled'   ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                                row.reason === 'receiver_finished'   ? 'bg-green-100 text-green-800 border-green-300' :
                                row.reason === 'session_resume_failed' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                                'bg-gray-100 text-gray-700 border-gray-300';
                            return (
                                <span key={i} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-semibold border ${color}`}>
                                    {row.reason}
                                    <span className="font-bold">{row.count}</span>
                                    <span className="font-normal opacity-70">({pct}%)</span>
                                </span>
                            );
                        })}
                    </div>

                    {/* Detail sub-table */}
                    {ccDisconnectReasons.by_detail?.length > 0 && (
                        <div className="overflow-x-auto mb-4">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Detail</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {ccDisconnectReasons.by_detail.map((row, i) => (
                                        <tr key={i} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 font-mono text-xs text-gray-800">{row.reason}</td>
                                            <td className="px-4 py-2 text-xs text-gray-600">{row.detail}</td>
                                            <td className="px-4 py-2 font-semibold">{row.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* By CC device name */}
                    {ccDisconnectReasons.by_device?.length > 0 && (
                        <div className="mb-4">
                            <h4 className="text-sm font-bold text-gray-700 mb-2">By Chromecast Device</h4>
                            <div className="flex flex-wrap gap-2">
                                {ccDisconnectReasons.by_device.map((row, i) => (
                                    <span key={i} className="inline-block px-2 py-1 rounded bg-blue-50 text-blue-800 text-xs">
                                        <strong>{row.device_name}</strong>: {row.count}x
                                        {row.reasons && (
                                            <span className="ml-1 opacity-70">({row.reasons})</span>
                                        )}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Recent events */}
                    {ccDisconnectReasons.recent_events?.length > 0 && (
                        <details className="mt-2">
                            <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                                Recent events ({ccDisconnectReasons.recent_events.length})
                            </summary>
                            <div className="mt-2 max-h-64 overflow-y-auto">
                                {ccDisconnectReasons.recent_events.map((evt, i) => (
                                    <div key={i} className="py-1 border-b border-gray-100 text-xs flex flex-wrap gap-x-2">
                                        <span className="text-gray-400">{new Date(evt.timestamp).toLocaleString()}</span>
                                        <span className={`font-semibold ${
                                            evt.properties?.reason === 'receiver_abort' ? 'text-red-600' :
                                            evt.properties?.reason === 'session_suspended' ? 'text-orange-600' :
                                            evt.properties?.reason === 'session_ended' ? 'text-blue-600' :
                                            'text-gray-700'
                                        }`}>{evt.properties?.reason}</span>
                                        {evt.properties?.detail && (
                                            <span className="text-gray-500">{evt.properties.detail}</span>
                                        )}
                                        {evt.properties?.device_name && (
                                            <span className="text-blue-500">{evt.properties.device_name}</span>
                                        )}
                                        {(evt.phone_manufacturer || evt.phone_model) && (
                                            <span className="text-gray-400">[{evt.phone_manufacturer} {evt.phone_model}]</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                </div>
            )}

            {/* Remote Source Events (SMB/DLNA) */}
            {remoteSourceEvents && (
                <div className="bg-white shadow rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold mb-4">Remote Source Playback (SMB / DLNA Server)</h3>

                    {/* Download Stats */}
                    {remoteSourceEvents.downloadStats && (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                            <div className="bg-green-50 rounded p-3">
                                <div className="text-sm text-green-600">Downloads OK</div>
                                <div className="text-xl font-bold">{remoteSourceEvents.downloadStats.downloads_ok || 0}</div>
                            </div>
                            <div className="bg-red-50 rounded p-3">
                                <div className="text-sm text-red-600">Downloads Failed</div>
                                <div className="text-xl font-bold">{remoteSourceEvents.downloadStats.downloads_failed || 0}</div>
                            </div>
                            <div className="bg-blue-50 rounded p-3">
                                <div className="text-sm text-blue-600">Avg Speed</div>
                                <div className="text-xl font-bold">
                                    {remoteSourceEvents.downloadStats.avg_speed_kbps
                                        ? `${Math.round(remoteSourceEvents.downloadStats.avg_speed_kbps)} kbps`
                                        : '—'}
                                </div>
                            </div>
                            <div className="bg-purple-50 rounded p-3">
                                <div className="text-sm text-purple-600">Avg File Size</div>
                                <div className="text-xl font-bold">
                                    {remoteSourceEvents.downloadStats.avg_file_size
                                        ? `${Math.round(remoteSourceEvents.downloadStats.avg_file_size / 1024 / 1024)} MB`
                                        : '—'}
                                </div>
                            </div>
                            <div className="bg-gray-50 rounded p-3">
                                <div className="text-sm text-gray-600">Avg Duration</div>
                                <div className="text-xl font-bold">
                                    {remoteSourceEvents.downloadStats.avg_duration_ms
                                        ? `${Math.round(remoteSourceEvents.downloadStats.avg_duration_ms / 1000)}s`
                                        : '—'}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Format Dialog Choices */}
                    {remoteSourceEvents.formatDialogChoices && remoteSourceEvents.formatDialogChoices.length > 0 && (
                        <div className="mb-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Format Incompatibility Choices</h4>
                            <div className="flex flex-wrap gap-2">
                                {remoteSourceEvents.formatDialogChoices.map((c, i) => (
                                    <span key={i} className={`px-3 py-1 rounded-full text-sm font-medium ${
                                        c.choice === 'download_transcode' ? 'bg-blue-100 text-blue-800' :
                                        c.choice === 'live_transcode' ? 'bg-yellow-100 text-yellow-800' :
                                        c.choice === 'offline_transcode' ? 'bg-purple-100 text-purple-800' :
                                        'bg-gray-100 text-gray-800'
                                    }`}>
                                        {c.choice} {c.is_remote ? '(remote)' : '(local)'}: {c.count}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Event Aggregates */}
                    {remoteSourceEvents.aggregates && remoteSourceEvents.aggregates.length > 0 && (
                        <div className="mb-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Event Summary</h4>
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="text-left text-gray-500">
                                        <th className="pb-2">Event</th>
                                        <th className="pb-2">Result</th>
                                        <th className="pb-2">Source</th>
                                        <th className="pb-2">Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {remoteSourceEvents.aggregates.map((a, i) => (
                                        <tr key={i} className="border-t">
                                            <td className="py-1 font-mono text-xs">{a.event_type}</td>
                                            <td className="py-1">
                                                <span className={`px-2 py-0.5 rounded text-xs ${
                                                    a.result === 'success' ? 'bg-green-100 text-green-800' :
                                                    a.result === 'failed' ? 'bg-red-100 text-red-800' :
                                                    'bg-gray-100 text-gray-600'
                                                }`}>{a.result || a.choice || '—'}</span>
                                            </td>
                                            <td className="py-1 text-xs">{a.source_type || a.provider_type || '—'}</td>
                                            <td className="py-1 font-bold">{a.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Recent Events */}
                    {remoteSourceEvents.recentEvents && remoteSourceEvents.recentEvents.length > 0 && (
                        <details className="mt-4">
                            <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-800">
                                Recent Events ({remoteSourceEvents.recentEvents.length})
                            </summary>
                            <div className="mt-2 max-h-64 overflow-y-auto">
                                {remoteSourceEvents.recentEvents.map((ev, i) => (
                                    <div key={i} className="text-xs font-mono bg-gray-50 p-2 mb-1 rounded">
                                        <span className="text-gray-400">{new Date(ev.timestamp).toLocaleString()}</span>{' '}
                                        <span className="font-bold">{ev.event_type}</span>{' '}
                                        <span className="text-gray-600">{JSON.stringify(ev.properties)}</span>
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                </div>
            )}
        </div>
    );
}
